import { useRef, useCallback } from 'react';

interface DragState {
  active: boolean;
  startX: number;
  startY: number;
  startLeft: number;
  startTop: number;
  startWidth: number;
  startHeight: number;
  element: HTMLDivElement | null;
  corner: string | null;
  canvasW: number;
  canvasH: number;
  zoom: number;
}

const MIN_SIZE = 20;

export function useBboxDrag(zoom: number, onDragEnd?: (el: HTMLDivElement) => void) {
  const dragRef = useRef<DragState>({
    active: false,
    startX: 0,
    startY: 0,
    startLeft: 0,
    startTop: 0,
    startWidth: 0,
    startHeight: 0,
    element: null,
    corner: null,
    canvasW: 0,
    canvasH: 0,
    zoom: 1,
  });
  const onDragEndRef = useRef(onDragEnd);
  onDragEndRef.current = onDragEnd;

  const startDrag = useCallback(
    (e: React.MouseEvent, div: HTMLDivElement, canvasW: number, canvasH: number) => {
      e.preventDefault();
      e.stopPropagation();
      const d = dragRef.current;
      d.active = true;
      d.element = div;
      d.corner = null;
      d.startX = e.clientX;
      d.startY = e.clientY;
      d.startLeft = parseInt(div.style.left) || 0;
      d.startTop = parseInt(div.style.top) || 0;
      d.startWidth = div.offsetWidth;
      d.startHeight = div.offsetHeight;
      d.canvasW = canvasW;
      d.canvasH = canvasH;
      d.zoom = zoom;

      const onMove = (ev: MouseEvent) => {
        if (!d.element) return;
        const dx = (ev.clientX - d.startX) / d.zoom;
        const dy = (ev.clientY - d.startY) / d.zoom;
        let newLeft = d.startLeft + dx;
        let newTop = d.startTop + dy;

        // Clamp
        newLeft = Math.max(0, Math.min(newLeft, d.canvasW - d.startWidth));
        newTop = Math.max(0, Math.min(newTop, d.canvasH - d.startHeight));

        d.element.style.left = `${newLeft}px`;
        d.element.style.top = `${newTop}px`;
      };

      const onUp = () => {
        const el = d.element;
        d.active = false;
        d.element = null;
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
        if (el && onDragEndRef.current) onDragEndRef.current(el);
      };

      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    },
    [zoom],
  );

  const startResize = useCallback(
    (
      e: React.MouseEvent,
      div: HTMLDivElement,
      corner: string,
      canvasW: number,
      canvasH: number,
    ) => {
      e.preventDefault();
      e.stopPropagation();
      const d = dragRef.current;
      d.active = true;
      d.element = div;
      d.corner = corner;
      d.startX = e.clientX;
      d.startY = e.clientY;
      d.startLeft = parseInt(div.style.left) || 0;
      d.startTop = parseInt(div.style.top) || 0;
      d.startWidth = div.offsetWidth;
      d.startHeight = div.offsetHeight;
      d.canvasW = canvasW;
      d.canvasH = canvasH;
      d.zoom = zoom;

      const onMove = (ev: MouseEvent) => {
        if (!d.element) return;
        const dx = (ev.clientX - d.startX) / d.zoom;
        const dy = (ev.clientY - d.startY) / d.zoom;

        let newLeft = d.startLeft;
        let newTop = d.startTop;
        let newW = d.startWidth;
        let newH = d.startHeight;

        if (corner === 'se') {
          newW = Math.max(MIN_SIZE, d.startWidth + dx);
          newH = Math.max(MIN_SIZE, d.startHeight + dy);
        } else if (corner === 'sw') {
          newLeft = d.startLeft + dx;
          newW = Math.max(MIN_SIZE, d.startWidth - dx);
          newH = Math.max(MIN_SIZE, d.startHeight + dy);
          if (newW === MIN_SIZE) newLeft = d.startLeft + d.startWidth - MIN_SIZE;
        } else if (corner === 'ne') {
          newW = Math.max(MIN_SIZE, d.startWidth + dx);
          newTop = d.startTop + dy;
          newH = Math.max(MIN_SIZE, d.startHeight - dy);
          if (newH === MIN_SIZE) newTop = d.startTop + d.startHeight - MIN_SIZE;
        } else if (corner === 'nw') {
          newLeft = d.startLeft + dx;
          newW = Math.max(MIN_SIZE, d.startWidth - dx);
          newTop = d.startTop + dy;
          newH = Math.max(MIN_SIZE, d.startHeight - dy);
          if (newW === MIN_SIZE) newLeft = d.startLeft + d.startWidth - MIN_SIZE;
          if (newH === MIN_SIZE) newTop = d.startTop + d.startHeight - MIN_SIZE;
        }

        // Clamp to canvas
        newLeft = Math.max(0, newLeft);
        newTop = Math.max(0, newTop);
        if (newLeft + newW > d.canvasW) newW = d.canvasW - newLeft;
        if (newTop + newH > d.canvasH) newH = d.canvasH - newTop;

        d.element.style.left = `${newLeft}px`;
        d.element.style.top = `${newTop}px`;
        d.element.style.width = `${newW}px`;
        d.element.style.height = `${newH}px`;
      };

      const onUp = () => {
        const el = d.element;
        d.active = false;
        d.element = null;
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
        if (el && onDragEndRef.current) onDragEndRef.current(el);
      };

      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    },
    [zoom],
  );

  return { startDrag, startResize };
}

/** Collect bbox positions from overlay divs and convert to normalized coordinates */
export function collectBboxes(
  containerEl: HTMLElement,
  canvasW: number,
  canvasH: number,
): { type: string; bbox: [number, number, number, number]; label?: string }[] {
  const overlays = containerEl.querySelectorAll<HTMLDivElement>('.bbox-overlay');
  const result: { type: string; bbox: [number, number, number, number]; label?: string }[] = [];

  overlays.forEach((div) => {
    const left = parseInt(div.style.left) || 0;
    const top = parseInt(div.style.top) || 0;
    const w = div.offsetWidth;
    const h = div.offsetHeight;

    const x_min = left / canvasW;
    const y_min = top / canvasH;
    const x_max = (left + w) / canvasW;
    const y_max = (top + h) / canvasH;

    result.push({
      type: div.dataset.bboxType || 'content',
      bbox: [x_min, y_min, x_max, y_max],
      label: div.dataset.bboxLabel,
    });
  });

  return result;
}
